/**
 * Admin Contact Submissions - Olympic Fit Wellness
 * Connected to live Supabase APIs
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Search,
  Mail,
  Phone,
  Clock,
  Check,
  AlertCircle
} from 'lucide-react';
import api from '../../lib/api';

export default function ContactsAdminPage() {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState(null);

  const loadContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getAdminContacts();
      setContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const filteredContacts = contacts.filter(contact => {
    if (filterStatus !== 'all' && contact.status !== filterStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (contact.name?.toLowerCase() || '').includes(query) ||
             (contact.email?.toLowerCase() || '').includes(query) ||
             (contact.subject?.toLowerCase() || '').includes(query);
    }
    return true;
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      default: return 'bg-neutral-600 text-neutral-300';
    }
  };

  const updateStatus = async (submissionId, newStatus) => {
    setUpdatingId(submissionId);
    try {
      await api.updateContactStatus(submissionId, newStatus);
      // Update local state
      setContacts(contacts.map(c => 
        c.submission_id === submissionId ? { ...c, status: newStatus } : c
      ));
    } catch (err) {
      console.error('Update error:', err);
      setError('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="contacts-admin">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-white">Contact Submissions</h1>
        <p className="text-neutral-400">Manage contact form submissions</p>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-neutral-800 rounded-xl p-4 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-neutral-800 rounded-xl p-4 border border-neutral-700">
          <p className="text-neutral-400 text-sm">New</p>
          <p className="text-2xl font-bold text-blue-400">
            {contacts.filter(c => c.status === 'new').length}
          </p>
        </div>
        <div className="bg-neutral-800 rounded-xl p-4 border border-neutral-700">
          <p className="text-neutral-400 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-yellow-400">
            {contacts.filter(c => c.status === 'in_progress').length}
          </p>
        </div>
        <div className="bg-neutral-800 rounded-xl p-4 border border-neutral-700">
          <p className="text-neutral-400 text-sm">Resolved</p>
          <p className="text-2xl font-bold text-green-400">
            {contacts.filter(c => c.status === 'resolved').length}
          </p>
        </div>
      </div>

      {/* Contacts List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredContacts.length > 0 ? (
        <div className="space-y-4">
          {filteredContacts.map((contact) => (
            <div key={contact.submission_id} className="bg-neutral-800 rounded-xl border border-neutral-700 p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{contact.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(contact.status)}`}>
                      {contact.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-[#3B82F6] font-medium">{contact.subject}</p>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-400">
                  <Clock className="w-4 h-4" />
                  {contact.created_at ? new Date(contact.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'N/A'}
                </div>
              </div>
              
              <p className="text-neutral-300 mb-4">{contact.message}</p>
              
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t border-neutral-700">
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-neutral-400 flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {contact.email}
                  </span>
                  {contact.phone && (
                    <span className="text-neutral-400 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {contact.status !== 'resolved' && (
                    <button
                      onClick={() => updateStatus(contact.submission_id, 'resolved')}
                      disabled={updatingId === contact.submission_id}
                      className="px-4 py-2 bg-green-500/20 text-green-400 text-sm font-medium rounded-lg hover:bg-green-500/30 flex items-center gap-1 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      Mark Resolved
                    </button>
                  )}
                  {contact.status === 'new' && (
                    <button
                      onClick={() => updateStatus(contact.submission_id, 'in_progress')}
                      disabled={updatingId === contact.submission_id}
                      className="px-4 py-2 bg-yellow-500/20 text-yellow-400 text-sm font-medium rounded-lg hover:bg-yellow-500/30 disabled:opacity-50"
                    >
                      Mark In Progress
                    </button>
                  )}
                  <a
                    href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}
                    className="px-4 py-2 bg-[#3B82F6] text-white text-sm font-medium rounded-lg hover:bg-blue-600"
                  >
                    Reply
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-12 text-center">
          <Mail className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Contact Submissions</h3>
          <p className="text-neutral-400">Contact form submissions will appear here.</p>
        </div>
      )}
    </div>
  );
}
